export type LoadingBarStatus = 'pending' | 'loading' | 'done' | 'idle' | 'error';
export type LoadingBarCallback = (status: LoadingBarStatus) => void;

class LoadingBarService {
  private static instance: LoadingBarService;
  private subscribers: LoadingBarCallback[] = [];
  private status: LoadingBarStatus = 'idle';
  private hasError: boolean = false;
  private toIdleTimeout?: NodeJS.Timeout;
  private timeToIdle: number = 3000;

  private constructor() { }

  public static getInstance(): LoadingBarService {
    if (!LoadingBarService.instance) {
      LoadingBarService.instance = new LoadingBarService();
    }
    return LoadingBarService.instance;
  }

  public setState(status: LoadingBarStatus): void {
    if(this.hasError) return;

    clearTimeout(this.toIdleTimeout);
    this.notifySubscribers(status);

    if(status === 'done'){
      this.toIdleTimeout = setTimeout(() => {
        this.notifySubscribers('idle');
      }, this.timeToIdle);
    } else if (status === 'error') {
      this.hasError = true;
    }
  }

  public getStatus(): LoadingBarStatus {
    return this.status;
  }

  public resetError(timeToIdle: number = 0): void {
    if(this.hasError){
      clearTimeout(this.toIdleTimeout);
      this.toIdleTimeout = setTimeout(()=>{
        this.hasError = false;
        this.notifySubscribers('idle');
      }, timeToIdle);
    }
  }

  /**
   * Subscribe to changes on the loading bar status.
   * Notifies immediately with the current status.
   * Accepts an optional AbortSignal to automatically unsubscribe when aborted.
   * @param callback A function that will be called whenever the status changes.
   * @param notify Whether to immediately notify the subscriber with the current status (defaults to true).
   * @param options Optional object containing an AbortSignal.
   * @returns An unsubscribe function.
   */
  public subscribeToStatus(
    callback: LoadingBarCallback,
    notify: boolean = true,
    options?: { signal?: AbortSignal }
  ): () => void {
    this.subscribers.push(callback);
    if (notify) callback(this.status);

    let abortHandler: () => void;

    const unsubscribe = () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
      if (options?.signal && abortHandler) {
        options.signal.removeEventListener('abort', abortHandler);
      }
    };

    if (options?.signal) {
      if (options.signal.aborted) {
        unsubscribe();
      } else {
        abortHandler = () => {
          unsubscribe();
        };
        options.signal.addEventListener('abort', abortHandler);
      }
    }
    return unsubscribe;
  }

  private notifySubscribers(status: LoadingBarStatus): void {
    this.status = status;
    this.subscribers.forEach(callback => callback(status));
  }
}

export const loadingBarService = LoadingBarService.getInstance();
